import { Badge, Skeleton, Stack, Table, Text } from "@mantine/core";
import { graphql } from "gql.tada";
import { useQuery } from "@apollo/client/react";
import {
  toIsoTimestamp,
  toOptionalIsoTimestamp,
  toValidationJobStatus,
  VALIDATION_STATUS_COLORS,
} from "./validation.service";

const VALIDATION_JOBS_QUERY = graphql(`
  query ValidationJobs($stepRuleSetId: ID!) {
    stepRuleSetValidationJobs(
      stepRuleSetId: $stepRuleSetId
      page: { first: 20 }
    ) {
      nodes {
        id
        status
        stage
        errorSummary
        startedAt
        completedAt
        progress {
          totalMutations
          rulesFired
          totalRulesDeclared
          elapsedMs
        }
        result {
          success
          durationMs
          totalMutations
        }
      }
    }
  }
`);

interface JobRow {
  readonly id: string;
  readonly status: string;
  readonly stage: string;
  readonly errorSummary: string | null | undefined;
  readonly startedAt: string;
  readonly completedAt: string | null | undefined;
  readonly progress:
    | {
        readonly totalMutations: number;
        readonly rulesFired: number;
      }
    | null
    | undefined;
  readonly result:
    | {
        readonly success: boolean;
        readonly durationMs: number;
        readonly totalMutations: number;
      }
    | null
    | undefined;
}

interface ValidationJobHistoryTabProps {
  readonly stepRuleSetId: string;
  readonly onOpenJob: (jobId: string) => void;
}

function ValidationJobHistoryTab(props: ValidationJobHistoryTabProps) {
  const { data, loading, error } = useQuery(VALIDATION_JOBS_QUERY, {
    variables: { stepRuleSetId: props.stepRuleSetId },
  });

  if (loading && !data) {
    return (
      <Stack gap="xs">
        <Skeleton height={32} />
        <Skeleton height={32} />
        <Skeleton height={32} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Text c="red" size="sm">
        Failed to load validation history.
      </Text>
    );
  }

  const jobs: readonly JobRow[] = (
    data?.stepRuleSetValidationJobs?.nodes ?? []
  ).map<JobRow>((job) => ({
    id: job.id,
    status: job.status,
    stage: job.stage,
    errorSummary: job.errorSummary,
    startedAt: toIsoTimestamp(job.startedAt),
    completedAt: toOptionalIsoTimestamp(job.completedAt),
    progress: job.progress,
    result: job.result,
  }));

  if (jobs.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        No validation runs yet.
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Started</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Duration</Table.Th>
            <Table.Th>Mutations</Table.Th>
            <Table.Th>Rules fired</Table.Th>
            <Table.Th>Summary</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {jobs.map((job) => {
            const status = toValidationJobStatus(job.status);
            return (
              <Table.Tr
                key={job.id}
                onClick={() => props.onOpenJob(job.id)}
                style={{ cursor: "pointer" }}
              >
                <Table.Td>{new Date(job.startedAt).toLocaleString()}</Table.Td>
                <Table.Td>
                  <Badge
                    color={VALIDATION_STATUS_COLORS[status]}
                    variant="light"
                  >
                    {status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {job.result
                    ? formatMs(job.result.durationMs)
                    : job.completedAt
                      ? formatDuration(job.startedAt, job.completedAt)
                      : "—"}
                </Table.Td>
                <Table.Td>
                  {job.result?.totalMutations ??
                    job.progress?.totalMutations ??
                    0}
                </Table.Td>
                <Table.Td>{job.progress?.rulesFired ?? 0}</Table.Td>
                <Table.Td>
                  <Text size="sm" c={job.errorSummary ? "red" : "dimmed"}>
                    {job.errorSummary ?? "—"}
                  </Text>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

function formatDuration(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  return formatMs(ms);
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export { ValidationJobHistoryTab, VALIDATION_JOBS_QUERY };
